import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PaginationHelper from "../../helper/PaginationHelper";
import { getImageUrlHelper, truncateText } from "../../helper/helper";
import {
  createPost,
  deletePost,
  getContentCategories,
  getPosts,
  updatePost,
} from "../../redux/content/contentSlice";

const PostLayer = () => {
  const dispatch = useDispatch();
  const { categories, posts, postsTotal } = useSelector((state) => state.content);

  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterSubCategoryId, setFilterSubCategoryId] = useState("");
  const [filterFeatured, setFilterFeatured] = useState("");

  const [editId, setEditId] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [isView, setIsView] = useState(false);

  useEffect(() => {
    dispatch(getContentCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPosts({
      page: currentPage,
      limit: entries,
      categoryId: filterCategoryId || undefined,
      subCategoryId: filterSubCategoryId || undefined,
      isFeatured: filterFeatured === "" ? undefined : filterFeatured,
    }));
  }, [dispatch, currentPage, entries, filterCategoryId, filterSubCategoryId, filterFeatured]);

  const selectedCategory = categories.find((item) => item.id === categoryId);
  const formSubCategories = selectedCategory?.subCategories || selectedCategory?.subcategories || [];

  const filterCategory = categories.find((item) => item.id === filterCategoryId);
  const filterSubCategories = filterCategory?.subCategories || filterCategory?.subcategories || [];

  const rows = useMemo(() => {
    return posts.filter((item) => {
      const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [posts, search]);

  const totalEntries = postsTotal || rows.length;
  const totalPages = Math.ceil(totalEntries / entries);

  const resetForm = () => {
    setEditId(null);
    setIsView(false);
    setCategoryId("");
    setSubCategoryId("");
    setTitle("");
    setDescription("");
    setIsFeatured(false);
    setLink("");
    setFile(null);
    setFilePreview("");
  };

  const getPostImage = (post) => post.path || post.file || post.image || post.thumbnail || post.media || post.url || post.imageUrl || "";

  const openView = (post) => {
    setEditId(post.id);
    setIsView(true);
    setCategoryId(post.categoryId || post.category?.id || "");
    setSubCategoryId(post.subCategoryId || post.subcategoryId || post.subCategory?.id || post.subcategory?.id || "");
    setTitle(post.title || "");
    setDescription(post.description || "");
    setIsFeatured(Boolean(post.isFeatured));
    setLink(post.link || "");
    setFile(null);
    const postImage = getPostImage(post);
    setFilePreview(postImage ? getImageUrlHelper(postImage) : "");
  };

  const openEdit = (post) => {
    setEditId(post.id);
    setIsView(false);
    setCategoryId(post.categoryId || post.category?.id || "");
    setSubCategoryId(post.subCategoryId || post.subcategoryId || post.subCategory?.id || post.subcategory?.id || "");
    setTitle(post.title || "");
    setDescription(post.description || "");
    setIsFeatured(Boolean(post.isFeatured));
    setLink(post.link || "");
    setFile(null);
    const postImage = getPostImage(post);
    setFilePreview(postImage ? getImageUrlHelper(postImage) : "");
  };

  const handleSubmit = () => {
    if (!categoryId) return toast.error("Category is required");
    if (!subCategoryId) return toast.error("Subcategory is required");
    if (!title.trim()) return toast.error("Title is required");
    if (!description.trim()) return toast.error("Description is required");
    if (!link.trim()) return toast.error("Link is required");
    if (!editId && !file) return toast.error("File is required");

    const payload = {
      categoryId,
      subCategoryId,
      title: title.trim(),
      description: description.trim(),
      isFeatured,
      link: link.trim(),
      file,
    };

    const action = editId ? updatePost({ id: editId, data: payload }) : createPost(payload);

    dispatch(action)
      .unwrap()
      .then((res) => {
        toast.success(res?.message || (editId ? "Updated successfully" : "Created successfully"));
        dispatch(getPosts({
          page: currentPage,
          limit: entries,
          categoryId: filterCategoryId || undefined,
          subCategoryId: filterSubCategoryId || undefined,
          isFeatured: filterFeatured === "" ? undefined : filterFeatured,
        }));
        resetForm();
        document.querySelector("#postModal .btn-close")?.click();
      })
      .catch((err) => toast.error(err?.message || "Something went wrong"));
  };

  const handleDelete = (id) => {
    dispatch(deletePost(id))
      .unwrap()
      .then(() => toast.success("Deleted successfully"))
      .catch((err) => toast.error(err?.message || "Something went wrong"));
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between flex-wrap gap-3">
          <div className="d-flex gap-3 flex-wrap">
            <select className="form-select form-select-sm w-auto" value={entries} onChange={(e) => setEntries(Number(e.target.value))}>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>
            <input className="form-control w-auto" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className="form-select w-auto" value={filterCategoryId} onChange={(e) => {
              setFilterCategoryId(e.target.value);
              setFilterSubCategoryId("");
              setCurrentPage(1);
            }}>
              <option value="">All Categories</option>
              {categories.map((item) => (
                <option key={item.id} value={item.id}>{item.title}</option>
              ))}
            </select>
            {/* <select className="form-select w-auto" value={filterSubCategoryId} onChange={(e) => {
              setFilterSubCategoryId(e.target.value);
              setCurrentPage(1);
            }}>
              <option value="">All Subcategories</option>
              {filterSubCategories.map((item) => (
                <option key={item.id} value={item.id}>{item.title}</option>
              ))}
            </select>
            <select className="form-select w-auto" value={filterFeatured} onChange={(e) => {
              setFilterFeatured(e.target.value);
              setCurrentPage(1);
            }}>
              <option value="">Featured</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select> */}
          </div>
          <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#postModal" onClick={resetForm}>
            + Add Post
          </button>
        </div>

        <div className="card-body p-12">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Subcategory</th>
                  <th>Featured</th>
                  <th>Link</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, i) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * entries + i + 1}</td>
                    <td>{truncateText(item.title, 28)}</td>
                    <td>{truncateText(item.description, 35)}</td>
                    <td>{truncateText(item.category?.title || item.Category?.title || "-", 32)}</td>
                    <td>{truncateText(item.subCategory?.title || item.subcategory?.title || item.SubCategory?.title || "-", 32)}</td>
                    <td>
                      <span className={`badge ${item.isFeatured ? "bg-success-focus text-success-600" : "bg-neutral-200 text-neutral-600"}`}>
                        {item.isFeatured ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noreferrer"
                          className=" text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="lucide:external-link" />
                        </a>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" data-bs-toggle="modal" data-bs-target="#postModal" onClick={() => openView(item)}>
                          <Icon icon="majesticons:eye-line" />
                        </button>
                        {/* <button className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" data-bs-toggle="modal" data-bs-target="#postModal" onClick={() => openEdit(item)}>
                          <Icon icon="lucide:edit" />
                        </button> */}
                        <button className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" onClick={() => handleDelete(item.id)}>
                          <Icon icon="lucide:trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationHelper currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={totalEntries} pageSize={entries} />
        </div>
      </div>

      <div className="modal fade" id="postModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5>{isView ? "View" : editId ? "Edit" : "Add"} Post</h5>
              <button className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              <label>Category <span className="text-danger">*</span></label>
              <select className="form-select mb-2" value={categoryId} disabled={isView} onChange={(e) => {
                setCategoryId(e.target.value);
                setSubCategoryId("");
              }}>
                <option value="">Select category</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>{item.title}</option>
                ))}
              </select>
              <label>Subcategory <span className="text-danger">*</span></label>
              <select className="form-select mb-2" value={subCategoryId} disabled={isView} onChange={(e) => setSubCategoryId(e.target.value)}>
                <option value="">Select subcategory</option>
                {formSubCategories.map((item) => (
                  <option key={item.id} value={item.id}>{item.title}</option>
                ))}
              </select>
              <label>Title <span className="text-danger">*</span></label>
              <input className="form-control mb-2" value={title} disabled={isView} onChange={(e) => setTitle(e.target.value)} placeholder="Enter title" />
              <label>Description <span className="text-danger">*</span></label>
              <textarea className="form-control mb-2" rows={4} value={description} disabled={isView} onChange={(e) => setDescription(e.target.value)} placeholder="Enter description" />
              <div className="form-check mb-2">
                <input className="form-check-input" id="isFeatured" type="checkbox" checked={isFeatured} disabled={isView} onChange={(e) => setIsFeatured(e.target.checked)} />
                <label className="form-check-label" htmlFor="isFeatured">Featured</label>
              </div>
              <label>Link <span className="text-danger">*</span></label>
              <input className="form-control mb-2" value={link} disabled={isView} onChange={(e) => setLink(e.target.value)} placeholder="Enter link" />
              <label>File {!editId && <span className="text-danger">*</span>}</label>
              {!isView && (
                <input className="form-control" type="file" accept="image/*" onChange={(e) => {
                  const selectedFile = e.target.files?.[0];
                  setFile(selectedFile);
                  setFilePreview(selectedFile ? URL.createObjectURL(selectedFile) : "");
                }} />
              )}
              {filePreview && (
                <img
                  src={filePreview}
                  alt="Preview"
                  className="mt-3 radius-8 w-100"
                  style={{ maxHeight: 220, objectFit: "contain" }}
                />
              )}
            </div>
            {!isView && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleSubmit}>{editId ? "Update" : "Save"}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PostLayer;
